import { useCallback, useEffect, useRef, useState } from 'react';
import { priceProposalsApi } from '../services/price-proposals-api';
import { EVENTO_CAMBIO_PROPIO, hayCambioPropioEnCurso } from '../lib/cambio-propio';

/** D-R3-2: cada 30 s y al volver a la pestaña del navegador. */
export const CADA_CUANTO_MS = 30_000;

/**
 * Spec 275 (R3): ¿cambió algo desde que se cargó? No recarga nada solo (D-R3-3):
 * dice que hay cambios; `aceptar` vuelve a tomar la firma (lo llama «Recargar»).
 */
export function useCambiosNuevos(): { hay: boolean; aceptar: () => void } {
  const [hay, setHay] = useState(false);
  const base = useRef<string | null>(null);
  // Ronda de R3: cada acción propia (retomar) abre una generación nueva. Un
  // sondeo que salió antes y vuelve después trae una firma de otra generación
  // (quizá con la acción propia a medias): se descarta.
  const generacion = useRef(0);
  // La generación del sondeo en vuelo: volver a la pestaña dispara
  // visibilitychange y focus a la vez, y alcanza con un sondeo.
  const enVuelo = useRef<number | null>(null);
  const mirar = useCallback(async () => {
    // I13: con una acción propia en curso no se mira (lo que cambia es de uno).
    if (hayCambioPropioEnCurso()) return;
    const gen = generacion.current;
    if (enVuelo.current === gen) return;
    enVuelo.current = gen;
    try {
      const { firma } = await priceProposalsApi.version();
      // Ronda de R3: lo que volvió durante o después de una acción propia no vale.
      if (gen !== generacion.current || hayCambioPropioEnCurso()) return;
      if (base.current == null) base.current = firma;
      else if (firma !== base.current) setHay(true);
    } catch {
      // Sin red o sin sesión: se vuelve a mirar en la próxima.
    } finally {
      if (enVuelo.current === gen) enVuelo.current = null;
    }
  }, []);
  /**
   * Después de una acción propia, vuelve a tomar la firma. Ronda de R3: no
   * apaga un aviso ya mostrado (era de otra persona); eso lo hace «Recargar».
   */
  const retomar = useCallback(() => {
    generacion.current++;
    base.current = null;
    void mirar();
  }, [mirar]);
  /** «Recargar»: apaga el aviso y vuelve a tomar la firma. */
  const aceptar = useCallback(() => {
    setHay(false);
    retomar();
  }, [retomar]);
  useEffect(() => {
    void mirar();
    const t = setInterval(() => {
      if (!document.hidden) void mirar();
    }, CADA_CUANTO_MS);
    const alVolver = () => {
      if (!document.hidden) void mirar();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    window.addEventListener(EVENTO_CAMBIO_PROPIO, retomar);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      window.removeEventListener(EVENTO_CAMBIO_PROPIO, retomar);
    };
  }, [mirar, retomar]);
  return { hay, aceptar };
}
